import React, { useState, useEffect } from 'react';
import { Box, Typography, keyframes } from '@mui/material';

// Spinning animation
const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Alternative wobble animation for variety
const wobbleAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(5deg);
  }
  75% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
`;

// Nautical and marketing buzzwords in the spirit of "reticulating splines"
const LOADING_PHRASES = [
  "Calibrating market compass...",
  "Reticulating splines...",
  "Triangulating customer insights...",
  "Charting conversion currents...",
  "Navigating data archipelago...",
  "Adjusting revenue rudder...",
  "Calculating campaign coordinates...",
  "Hoisting performance metrics...",
  "Plotting profit trajectories...",
  "Anchoring audience analytics...",
  "Sailing through SKU seas...",
  "Docking demographic data...",
  "Weighing margin moorings...",
  "Unfurling funnel forecasts...",
  "Steering strategic scenarios...",
  "Casting conversion nets...",
  "Tacking through trend data...",
  "Measuring marketing monsoons...",
  "Charting channel channels...",
  "Navigating brand boundaries...",
  "Plotting price points...",
  "Surveying sales soundings...",
  "Mapping monetization routes...",
  "Calculating cohort currents...",
  "Aligning attribution anchors..."
];

const HelmSpinner = ({ 
  size = 40, 
  color = '#47BDC6', 
  animationType = 'spin', // 'spin' or 'wobble'
  speed = '1s',
  showText = false,
  minDuration = 5000 // Minimum 5 seconds
}) => {
  const [currentPhrase, setCurrentPhrase] = useState(LOADING_PHRASES[0]);
  const [startTime] = useState(Date.now());
  const animation = animationType === 'wobble' ? wobbleAnimation : spinAnimation;

  useEffect(() => {
    if (!showText) return;

    const phraseInterval = setInterval(() => {
      const randomPhrase = LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)];
      setCurrentPhrase(randomPhrase);
    }, 1500); // Change phrase every 1.5 seconds

    return () => clearInterval(phraseInterval);
  }, [showText]);

  // Ensure minimum duration
  useEffect(() => {
    if (minDuration > 0) {
      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        // Component should stay visible for at least minDuration
        const remainingTime = minDuration - elapsed;
        const timer = setTimeout(() => {
          // This will be handled by parent component
        }, remainingTime);
        return () => clearTimeout(timer);
      }
    }
  }, [startTime, minDuration]);
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        sx={{
          width: size,
          height: size,
          animation: `${animation} ${speed} linear infinite`,
          display: 'inline-block',
          mb: showText ? 2 : 0
        }}
      >
        <img 
          src="/favicon.svg" 
          alt="Loading..." 
          style={{ 
            width: '100%', 
            height: '100%',
            filter: color !== '#47BDC6' ? `hue-rotate(${getHueRotation(color)}deg)` : 'none'
          }}
        />
      </Box>
      
      {showText && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontStyle: 'italic',
            textAlign: 'center',
            minHeight: 20,
            animation: 'fadeIn 0.3s ease-in-out'
          }}
        >
          {currentPhrase}
        </Typography>
      )}
    </Box>
  );
};

// Helper function to convert colors to hue rotation (simplified)
const getHueRotation = (color) => {
  const colorMap = {
    '#845BA3': 280, // Purple
    '#EBDD71': 50,  // Yellow
    '#FF6B6B': 0,   // Red
    '#4ECDC4': 180, // Teal
    '#45B7D1': 200  // Blue
  };
  return colorMap[color] || 0;
};

export default HelmSpinner;
